import '@app/App.css';
import HomeHeader from '@app/HomeHeader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@comps/ui/dialog';
import { isCompozzError } from '@dto/CompozzError';
import { Workspace } from '@dto/workspaces';
import { IsDarkColor } from '@utils/colors';
import { BASE_URL, SELECTED_WORKSPACE_ID } from '@utils/constants';
import { CIcon, getRandomIcon } from '@utils/icons';
import { getSelectedDashboardKey } from '@utils/localStorage';
import { IconSax } from '@utils/saxIcons';
import { TW_App, TW_HomeWelcomeText, TW_HomeWorkspaces, TW_HomeWorkspacesCreation, TW_HomeWorkspacesCreationItem, TW_HomeWorkspacesCreationItemDescription, TW_HomeWorkspacesCreationItemIcon, TW_HomeWorkspacesCreationItemTitle, TW_HomeWorkspacesItem, TW_HomeWorkspacesItemActionButton, TW_HomeWorkspacesItemContainer, TW_HomeWorkspacesItemDragging, TW_HomeWorkspacesItemIcon, TW_HomeWorkspacesItemIconContainer, TW_HomeWorkspacesItemText, TW_HomeWorkspacesItemTextDescription, TW_HomeWorkspacesItemTextTitle, TW_HomeWorkspacesList, TW_HomeWorkspacesTitle } from '@utils/twClasses';
import { get, post } from '@utils/utils';
import { enUS, fr } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translate } from 'src/i18n/i18n';
import { useAppStore, useModelStore, useUserStore } from 'src/store';
import { useWorkspacesReorder } from './useWorkspacesReorder';
import WorkspaceActionButton from './WorkspaceActionButton';
import WorkspaceDropLine from './WorkspaceDropLine';
import WorkspaceForm, { CallBackEnd } from './WorkspaceForm';
import WorkspaceImportWorkspaceDialogContent from './WorkspaceImportWorkspaceDialogContent';
import WorkspaceTemplatesDialogContent from './WorkspaceTemplatesDialogContent';

function WorkspaceManagement() {

  const setWorkspaces = useModelStore((state) => state.updateWorkspaces);
  const workspaces = useModelStore((state) => state.workspaces);
  const updateLocales = useAppStore(state => state.updateLocales);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isScratchDialogOpen, setIsScratchDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const user = useUserStore((state) => state.user);
  const tenant = useAppStore(state => state.tenant);
  const wsColor = useModelStore((state) => state.wsColor);
  const wsDarkColor = useModelStore((state) => state.wsDarkColor);
  const wsLightColor = useModelStore((state) => state.wsLightColor);
  // Drag & drop of the cards to change the order of the workspaces (owners of the tenant only)
  const { draggedKey, dropTarget, listDragProps, cardDragProps } = useWorkspacesReorder();

  useEffect(() => {
    updateLocales(new Map([['fr-FR', fr], ['en-EN', enUS]]));
  }, []);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const response = await get<Workspace[]>(`${BASE_URL}/model/workspaces`);
        if (isCompozzError(response)) {
          throw response;
        }
        const workspaces = response as Workspace[];
        if (workspaces && workspaces.length > 0) {
          setWorkspaces(workspaces);
        }
      } catch (error) {
        console.error('Error fetching workspaces:', error);
        setWorkspaces([]);
      }
    };

    fetchWorkspaces();
  }, []);

  function addWorkspace(workspace: Workspace) {
    const newWorkspaces = [...workspaces, workspace];
    setWorkspaces(newWorkspaces);
    setIsScratchDialogOpen(false);
  }

  function cancel() {
    setIsScratchDialogOpen(false);
  }

  const newWorkspaceCall: CallBackEnd<Workspace> = (body: any) => {
    return post<Workspace>(`${BASE_URL}/model/workspaces`, body);
  }

  function handleWorkspaceCreated(workspace: Workspace) {
    handleWorkspacesCreated([workspace]);
  }

  // Several workspaces are created at once when workspaces which depend on each other are imported together
  function handleWorkspacesCreated(createdWorkspaces: Workspace[]) {
    setWorkspaces([...workspaces, ...createdWorkspaces]);
    setIsTemplateDialogOpen(false);
    setIsImportDialogOpen(false);
  }

  function enterWorkspace(workspaceKey: string) {
    const lastSelectedWorkspace = localStorage.getItem(SELECTED_WORKSPACE_ID);
    localStorage.setItem(SELECTED_WORKSPACE_ID, workspaceKey);
    if (lastSelectedWorkspace === workspaceKey) {
      const selectedDashboard = getSelectedDashboardKey(workspaceKey);
      if (selectedDashboard) {
        navigate(`/workspaces/${workspaceKey}/dashboards/${selectedDashboard}`);
      } else {
        navigate(`/workspaces/${workspaceKey}/objects`);
      }
    } else {
      navigate(`/workspaces/${workspaceKey}/objects`);
    }
  }


  var title = t('home.workspaces.title');
  if (tenant?.welcomeText) {
    title = translate(t, tenant.welcomeText, [user?.firstname]);
  }
  var welcomeTextColor = tenant?.welcomeTextColor ?? "#000000";
  var logoUrl = tenant?.logoUrl;
  
  const scrollbarStyle: React.CSSProperties = {
    scrollbarColor: `${wsDarkColor} ${wsLightColor}`,
  } as React.CSSProperties;
  
  return (
    <div className={TW_App} style={scrollbarStyle}>
      <HomeHeader />
      <section className={TW_HomeWorkspaces}>
        {logoUrl && (
          <div className="flex flex-row justify-between items-center gap-12 mb-16">
            <img src={logoUrl} alt="logo" className="w-32 h-auto" />
            <h1 className={TW_HomeWelcomeText} style={{ color: welcomeTextColor }}>{title}</h1>
          </div>
        )}
        {!logoUrl && (
          <h1 className={TW_HomeWorkspacesTitle}>{title}</h1>
        )}
        <ul className={TW_HomeWorkspacesList} {...listDragProps}>
          {workspaces.map((workspace) => {
            const iconBgColor = workspace.color ? workspace.color : "white";
            const iconColor = IsDarkColor(iconBgColor) ? "#ffffff" : "#000000";

            return (
              <li key={workspace._key} className={TW_HomeWorkspacesItem + (tenant?.customColor ? " hover:border-gray-300" : " hover:border-compozz-purple") + (draggedKey === workspace._key ? " " + TW_HomeWorkspacesItemDragging : "")}
                onClick={() => enterWorkspace(workspace._key)} {...cardDragProps(workspace)}>
                {dropTarget?.key === workspace._key && (
                  <WorkspaceDropLine position={dropTarget.position} singleColumn={dropTarget.singleColumn} color={wsColor} />
                )}
                <div className={TW_HomeWorkspacesItemContainer}>
                  <div className={TW_HomeWorkspacesItemIconContainer} style={{ backgroundColor: iconBgColor }}>
                    <CIcon compozzIcon={workspace.icon ?? getRandomIcon()} className={TW_HomeWorkspacesItemIcon} color={iconColor} />
                  </div>
                  <div className={TW_HomeWorkspacesItemText}>
                    <p className={TW_HomeWorkspacesItemTextTitle}>{workspace.name}</p>
                    <p className={TW_HomeWorkspacesItemTextDescription}>{workspace.description}</p>
                  </div>
                </div>
                {user?.owner && (
                  <WorkspaceActionButton workspace={workspace} className={TW_HomeWorkspacesItemActionButton + (tenant?.customColor ? " hover:text-gray-600" : " hover:text-compozz-purple")} />
                )}
              </li>
            )
          }
          )}
        </ul>
        {user?.owner && (
          <div className={TW_HomeWorkspacesCreation}>
            <Dialog open={isScratchDialogOpen} onOpenChange={setIsScratchDialogOpen}>
              <DialogTrigger className={TW_HomeWorkspacesCreationItem + (tenant?.customColor ? " hover:border-gray-300" : " hover:border-compozz-purple")}>
                <IconSax iconName="grid-1" className={TW_HomeWorkspacesCreationItemIcon + " text-[#f25a49]"} />
                <p className={TW_HomeWorkspacesCreationItemTitle}>{t('home.workspaces.creation.scratch.title')}</p>
                <p className={TW_HomeWorkspacesCreationItemDescription}>{t('home.workspaces.creation.scratch.description')}</p>
              </DialogTrigger>
              <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                  <DialogTitle>{t("workspace.dialog.new.title")}</DialogTitle>
                  <DialogDescription className="hidden">{t("workspace.dialog.new.description")}</DialogDescription>
                </DialogHeader>
                <WorkspaceForm initialWorkspace={null} callBackEnd={newWorkspaceCall} onValidate={addWorkspace} onCancel={cancel} />
              </DialogContent>
            </Dialog>
            <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
              <DialogTrigger className={TW_HomeWorkspacesCreationItem + (tenant?.customColor ? " hover:border-gray-300" : " hover:border-compozz-purple")} disabled={false}>
                <IconSax iconName="element-equal" className={TW_HomeWorkspacesCreationItemIcon + " text-compozz-dark-blue"} />
                <p className={TW_HomeWorkspacesCreationItemTitle}>{t('home.workspaces.creation.template.title')}</p>
                <p className={TW_HomeWorkspacesCreationItemDescription}>{t('home.workspaces.creation.template.description')}</p>
              </DialogTrigger>
              <WorkspaceTemplatesDialogContent onWorkspaceCreated={handleWorkspaceCreated} />
            </Dialog>
            <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
              <DialogTrigger className={TW_HomeWorkspacesCreationItem + (tenant?.customColor ? " hover:border-gray-300" : " hover:border-compozz-purple")} disabled={false}>
                <IconSax iconName="receive-square" className={TW_HomeWorkspacesCreationItemIcon + " text-compozz-purple"} />
                <p className={TW_HomeWorkspacesCreationItemTitle}>{t('home.workspaces.creation.import.title')}</p>
                <p className={TW_HomeWorkspacesCreationItemDescription}>{t('home.workspaces.creation.import.description')}</p>
              </DialogTrigger>
              <WorkspaceImportWorkspaceDialogContent onWorkspacesCreated={handleWorkspacesCreated} />
            </Dialog>
          </div>
        )}
      </section>
    </div>
  );
};

export default WorkspaceManagement;